import React, { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useRecoilState } from 'recoil';
import PropTypes from 'prop-types';
import { Button, Label, SelectDropdown } from '@gsa/afp-component-library';
import SelectMakeModelPanel from '../make-model/components/make-model-select-panel';
import CommentSection from '../make-model/make-model-modal-comment-section';
import { useMakeModelProvider } from '../Providers/make-model-panel-provider';
import {
  getAddMakeModelToast,
  getMakeModelErrorMessage,
} from '../components/make-model-approval-helper';
import { makeModelStatusMessageAtom } from '../atoms/make-model-atoms';
import {
  ADD_MAKE_MODEL_STATUS,
  MAKE_MODEL_ADD_SOURCE_OPTIONS,
} from '../constants';
import { ADD_MAKE_MODEL } from '../queries.gql';

const REQUIRED_MSG = 'This is a required field';

const AddMakeModelContent = ({ onClose, refetchData }) => {
  // VMS is the only selectable source today, so pre-select it by default.
  const [source, setSource] = useState(
    MAKE_MODEL_ADD_SOURCE_OPTIONS[0]?.value ?? '',
  );
  const [sourceError, setSourceError] = useState('');
  const [commentBody, setCommentBody] = useState('');
  const [commentError, setCommentError] = useState('');
  const [, setToaster] = useRecoilState(makeModelStatusMessageAtom);

  const {
    getErrors,
    setDirtyFields,
    bodyMake,
    bodyModel,
    bodyMakeInputValue,
    bodyModelInputValue,
  } = useMakeModelProvider();

  const modalSubTitle = 'Select a make before selecting model.';

  const [addMakeAndModel, { loading }] = useMutation(ADD_MAKE_MODEL, {
    onCompleted: (data) => {
      const result = data?.addMakeModelByEngineer || {};
      const { status } = result;
      onClose();
      // Fall back to the entered names if the backend omits them.
      setToaster(
        getAddMakeModelToast(
          status,
          result.makeName || bodyMakeInputValue,
          result.modelName || bodyModelInputValue,
        ),
      );
      // Only a genuine insert changes the table; skip refetch on ALREADY_*.
      if (!status || status === ADD_MAKE_MODEL_STATUS.SUCCESS) {
        refetchData();
      }
    },
    onError: (err) => {
      onClose();
      setToaster({
        type: 'error',
        message: getMakeModelErrorMessage(
          err.message,
          'Add',
          bodyMakeInputValue,
          bodyModelInputValue,
        ),
      });
    },
  });

  const onSourceChange = ({ target: { value } }) => {
    setSource(value);
    setSourceError(value ? '' : REQUIRED_MSG);
  };

  const onCommentChange = (value) => {
    setCommentBody(value);
    setCommentError(value?.trim() ? '' : REQUIRED_MSG);
  };

  const handleSave = useCallback(async () => {
    // Surface make/model typeahead errors by marking them dirty.
    setDirtyFields(['bodyMake', 'bodyModel']);
    const makeModelErrors = getErrors();

    let hasError = false;
    if (!source) {
      setSourceError(REQUIRED_MSG);
      hasError = true;
    }
    if (!commentBody?.trim()) {
      setCommentError(REQUIRED_MSG);
      hasError = true;
    }
    if (makeModelErrors?.length || hasError) return;

    const formData = {
      makeName: bodyMakeInputValue?.trim(),
      makeCode: bodyMake?.value ? Number(bodyMake.value) : null,
      modelName: bodyModelInputValue?.trim(),
      modelCode: bodyModel?.value || null,
      entrySource: Number(source),
      comment: commentBody.trim(),
    };

    addMakeAndModel({
      variables: {
        addMakeModelByEngineerInput: formData,
      },
    });
  }, [
    getErrors,
    setDirtyFields,
    source,
    commentBody,
    bodyMake,
    bodyModel,
    bodyMakeInputValue,
    bodyModelInputValue,
    addMakeAndModel,
  ]);

  return (
    <>
      <div className="margin-top-4 margin-bottom-6">
        <span tabIndex="0" aria-label={modalSubTitle}>
          {modalSubTitle}
        </span>
        <div className="grid-row margin-top-4">
          <div className="grid-col-12">
            <div className="bg-primary-lighter padding-2 radius-md">
              <SelectMakeModelPanel />
            </div>
          </div>
        </div>
        <div className="grid-row margin-top-4 padding-left-2">
          <div className="grid-col-4">
            <Label className="text-bold" required>
              Source
            </Label>
            <SelectDropdown
              data-testid="add-make-model-source-dropdown"
              aria-label="Source"
              aria-required="true"
              name="add-make-model-source"
              required
              value={source}
              onChange={onSourceChange}
              errorMessage={sourceError}
              options={[...MAKE_MODEL_ADD_SOURCE_OPTIONS]}
            />
          </div>
        </div>
        <div className="grid-row margin-top-4 padding-left-2">
          <div className="grid-col-12">
            <CommentSection
              commentBody={commentBody}
              onCommentChange={onCommentChange}
              required
              errorMessage={commentError}
            />
          </div>
        </div>
      </div>
      <div className="flex-align-start flex-justify-start">
        <Button
          data-testid="add-make-model-cancel-btn"
          variant="unstyled"
          onClick={onClose}
          className="margin-right-2"
          label="Cancel"
        />
        <Button
          data-testid="add-make-model-save-btn"
          variant="primary"
          onClick={handleSave}
          label={loading ? 'Saving...' : 'Save and approve'}
        />
      </div>
    </>
  );
};

AddMakeModelContent.propTypes = {
  onClose: PropTypes.func.isRequired,
  refetchData: PropTypes.func.isRequired,
};

export default AddMakeModelContent;
